import { inject as service } from '@ember/service';
import Model from '@ember-data/model';
import DS from 'ember-data';
import attr from 'ember-data/attr';
import { computed } from '@ember/object';
import { isEmpty } from '@ember/utils';
import { getLocaleByCode } from 'cio/utils/languages';
import SubscriptionCenterService from 'cio/services/subscription-center';
import isEqual from 'lodash.isequal';

interface SubscriptionLanguageTopic {
  id: number;
  name: string;
  description: string;
}

export default class SubscriptionLanguageModel extends Model {
  @service store!: DS.Store;
  @service subscriptionCenter!: SubscriptionCenterService;

  @attr('boolean', { defaultValue: () => false }) enabled!: boolean;
  @attr('array', { defaultValue: () => [] }) topics!: SubscriptionLanguageTopic[];
  @attr('string') title!: string;
  @attr('string') subtitle!: string;

  @computed('id')
  get countryCode() {
    if (!this.id.match(/-[A-z]{2}$/)) {
      return;
    }

    let splitLcid = this.id.split('-');

    return splitLcid[splitLcid.length - 1].toLowerCase();
  }

  @computed('id')
  get countryName() {
    if (isEmpty(this.id)) {
      return;
    }

    return getLocaleByCode(this.id)?.name;
  }

  get topicsById() {
    return Object.fromEntries(this.topics.map((topic) => [topic.id, topic]));
  }

  get isMissingContent() {
    return this.isHeaderMissingContent || this.isTopicsMissingContent || this.subscriptionCenter.hasNoTopics;
  }

  get isHeaderMissingContent() {
    let { preferencesPage } = this.subscriptionCenter;
    if (preferencesPage) {
      return (
        (!isEmpty(preferencesPage.header.title) && isEmpty(this.title)) ||
        (!isEmpty(preferencesPage.header.subtitle) && isEmpty(this.subtitle))
      );
    }
    return false;
  }

  get isTopicsMissingContent() {
    return this.missingTopicIds.length !== 0;
  }

  get missingTopicIds() {
    return this.store
      .peekAll('subscription-topic')
      .toArray()
      .filter((topic) => {
        let translatedTopic = this.topicsById[parseInt(topic.id)];

        if (translatedTopic == null) {
          return true;
        }

        return isEmpty(translatedTopic.name) || (!isEmpty(topic.description) && isEmpty(translatedTopic.description));
      })
      .map((topic) => topic.id);
  }

  @computed('enabled', 'title', 'subtitle', 'topics.@each.{id,name,description}')
  get isDirty() {
    return ['enabled', 'title', 'subtitle', 'topics'].some((prop) => {
      let changed = this.changedAttributes()[prop];

      if (changed == null) {
        return false;
      }

      let [prevValue, newValue] = changed;

      return !isEqual(prevValue, newValue);
    });
  }
}

// DO NOT DELETE: this is how TypeScript knows how to look up your models.
declare module 'ember-data/types/registries/model' {
  export default interface ModelRegistry {
    'subscription-language': SubscriptionLanguageModel;
  }
}
